/**
 * ProfessionalReviews
 * Display and manage reviews section for a professional
 */

import {
  ReviewForm,
  ReviewsList,
  useDeleteReview,
  useReviewsByProfessional,
} from '@features/reviews'
import { useAuth } from '@shared/contexts'
import { useConfirm } from '@shared/hooks'
import { Button } from '@shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@shared/components/ui/dialog'
import { actionStorage } from '@shared/lib/storage'
import type { Professional } from '@shared/types'
import { ChevronDown, ChevronUp, MessageSquare, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface ProfessionalReviewsProps {
  professional: Professional
}

// ============================================================================
// Component
// ============================================================================

export function ProfessionalReviews({ professional }: ProfessionalReviewsProps) {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: reviews, isLoading } = useReviewsByProfessional(professional.id)
  const deleteReview = useDeleteReview()
  const { confirm } = useConfirm()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  // Users can't review their own professional profile (backend rejects with
  // 422). The "Dejar Reseña" button stays visible, but when the form would
  // open on your own profile we show an explanatory message instead of the
  // form fields (see ReviewForm's isSelfReview prop).
  // Compare the viewer's own professional-profile id (User.professionalId) with
  // the profile being viewed. professional.userId is not populated by the
  // detail-page mapper, so it can't be used here.
  const isOwnProfile =
    isAuthenticated && !!user?.professionalId && user.professionalId === professional.id

  // Listen for pending review action events
  useEffect(() => {
    const handleOpenReviewDialog = (event: Event) => {
      const customEvent = event as CustomEvent<undefined | { type: string }>
      if (customEvent.detail?.type === 'create_review') {
        setIsDialogOpen(true)
      }
    }

    window.addEventListener('open-review-dialog', handleOpenReviewDialog)
    return () => {
      window.removeEventListener('open-review-dialog', handleOpenReviewDialog)
    }
  }, [])

  // Handle review button click with auth flow
  const handleLeaveReviewClick = (e: React.MouseEvent) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      e.preventDefault()
      // Store pending action
      actionStorage.setPendingAction({
        type: 'create_review',
        data: {
          professionalId: professional.id,
          professionalName: professional.professionalName
        },
        timestamp: Date.now(),
        returnPath: location.pathname,
        message: '¡Ahora podés dejar una reseña!'
      })
      toast.warning('Inicia sesión para dejar una reseña.')

      // Redirect to login
      void navigate('/login', {
        state: {
          from: location,
          message: ''
        }
      })
      return
    }

    // Check if account is validated
    if (!user?.isAccountValidated) {
      // TODO : Review if its worth to get validation status from backend

      e.preventDefault()
      // Store pending action
      actionStorage.setPendingAction({
        type: 'create_review',
        data: {
          professionalId: professional.id,
          professionalName: professional.professionalName
        },
        timestamp: Date.now(),
        returnPath: location.pathname,
        message: '¡Ahora podés dejar una reseña!'
      })

      toast.warning('Tenés que validar tu cuenta antes de dejar una reseña.')

      // Redirect to profile for validation
      void navigate('/profile', {
        state: {
          needsValidation: true,
          message: ''
        }
      })
      return
    }

    // All good - allow dialog to open
    setIsDialogOpen(true)
  }

  // Handle review form success
  const handleReviewSuccess = () => {
    setIsDialogOpen(false)
  }

  // Handle delete review
  const handleDeleteReview = async (reviewId: string) => {
    const confirmed = await confirm({
      title: 'Eliminar Reseña',
      description: '¿Estás seguro de que quieres eliminar esta reseña? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'destructive'
    })

    if (confirmed) {
      try {
        await deleteReview.mutateAsync({
          reviewId,
          professionalId: professional.id,
        })
      } catch (error) {
        logger.error('Failed to delete review:', error)
      }
    }
  }

  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className="flex flex-col gap-4">
          {/* Title and Chevron Row */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center w-full gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            aria-expanded={isExpanded}
            aria-controls="reviews-content"
          >
            <CardTitle className="flex items-center gap-2 w-full">
              <MessageSquare className="h-5 w-5" />
              <span>Reseñas</span>
            </CardTitle>

            {isExpanded ? (
              <ChevronUp className="h-5 w-5 cursor-pointer hover:opacity-80" onClick={() => setIsExpanded(!isExpanded)} />
            ) : (
              <ChevronDown className="h-5 w-5 cursor-pointer hover:opacity-80" onClick={() => setIsExpanded(!isExpanded)} />
            )}
          </button>

          {/* Buttons Row - Full Width */}
          <div className="flex gap-2 w-full">
            {/* Add Review Button - always visible; the form itself gates
                self-reviews with an explanatory message. */}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <Button
                className="flex-1"
                onClick={handleLeaveReviewClick}
              >
                <Plus className="h-4 w-4 mr-2" />
                Dejar Reseña
              </Button>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border border-border rounded-md">
                <DialogHeader>
                  <DialogTitle>
                    Dejar una Reseña
                  </DialogTitle>
                  <DialogDescription>
                    Comparte tu experiencia trabajando con {professional.professionalName}
                  </DialogDescription>
                </DialogHeader>
                <ReviewForm
                  professionalId={professional.id}
                  isSelfReview={isOwnProfile}
                  onSuccess={handleReviewSuccess}
                  onCancel={() => {
                    setIsDialogOpen(false)
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent id="reviews-content" className='pt-0'>
          <div className="flex flex-col gap-4">
            <ReviewsList
              reviews={reviews || []}
              isLoading={isLoading}
              currentUserId={user?.id}
              onDeleteReview={(id) => void handleDeleteReview(id)}
              itemsPerPage={5}
            />
          </div>
        </CardContent>
      )}
    </Card>
  )
}
